/* AQUON shared quick-cart helper.
   Custom sections that add to the cart with their own fetch should call
   window.AquonCart.open() (or addAndOpen) afterwards so the theme's cart
   drawer refreshes and auto-opens - exactly like the native add-to-cart.

   Why this exists: the drawer (<cart-drawer-component auto-open>) only opens
   when it receives a `cart:update` event whose detail carries data.itemCount.
   A bare `cart:update` (or a redirect to /cart) never opens the drawer. */
(function () {
  'use strict';

  var origFetch = window.fetch.bind(window);
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var refreshGeneration = 0;

  // A newer cart mutation/refresh must win over a slower earlier snapshot.
  document.addEventListener('cart:update', function (event) {
    if (!event.detail || event.detail.sourceId !== 'aquon-quick-cart') refreshGeneration++;
  });

  /* Ask the cart drawer + count components which section ids to re-render. */
  function gatherGroupedSectionIds() {
    var sections = [];
    var evt = new Event('cart:grouped-sections', { bubbles: true });
    evt.detail = { sections: sections };
    try { document.dispatchEvent(evt); } catch (e) {}
    var seen = {};
    var out = [];
    sections.forEach(function (id) {
      if (id && !seen[id]) { seen[id] = true; out.push(id); }
    });
    return out;
  }

  function fetchSectionsHtml(ids) {
    if (!ids.length) return Promise.resolve({});
    var basePath = window.location.pathname.split('?')[0];
    function fetchOne(id) {
      return origFetch(basePath + '?section_id=' + encodeURIComponent(id), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) { return [id, html]; })
        .catch(function () { return [id, '']; });
    }
    var batches = [];
    // Shopify's Section Rendering API accepts at most five IDs per request.
    for (var i = 0; i < ids.length; i += 5) batches.push(ids.slice(i, i + 5));
    return Promise.all(batches.map(function (batch) {
      return origFetch(basePath + '?sections=' + encodeURIComponent(batch.join(',')), { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : {}; })
        .catch(function () { return {}; })
        .then(function (sections) {
          // A successful HTTP response can still contain null for one section.
          // Retry only those missing fragments through the single-section API.
          return Promise.all(batch.map(function (id) {
            return sections && typeof sections[id] === 'string' && sections[id]
              ? [id, sections[id]] : fetchOne(id);
          }));
        });
    })).then(function (batchesOfPairs) {
      var out = {};
      batchesOfPairs.forEach(function (pairs) {
        pairs.forEach(function (p) { if (p[1]) out[p[0]] = p[1]; });
      });
      return out;
    });
  }

  /* Opens the drawer only when the theme is configured to pop it open after an
     add. `auto-open` is emitted by sections/cart-drawer.liquid from the
     `cart_auto_open` setting and is the same flag the theme's own cart-update
     handler checks, so one switch governs both paths. With it off the cart is
     still refreshed - the customer just is not interrupted. The header cart
     icon calls CartDrawerComponent.open() directly and always works. */
  function forceOpenDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && !drawer.hasAttribute('auto-open')) return;
    if (drawer && typeof drawer.showDialog === 'function') {
      var dlg = drawer.querySelector('[ref="dialog"], dialog');
      if (!dlg || !dlg.hasAttribute('open')) {
        try { drawer.showDialog(); } catch (e) {}
      }
    }
  }

  function cartUpdateEvent(cart, sections) {
    var evt = new Event('cart:update', { bubbles: true });
    evt.detail = {
      resource: cart,
      sourceId: 'aquon-quick-cart',
      data: {
        source: 'aquon-quick-cart',
        itemCount: (cart && cart.item_count) || 0,
        sections: Object.keys(sections).length ? sections : undefined,
        isIncremental: false
      }
    };
    return evt;
  }

  /* Refresh the cart drawer + count from the given cart object and open the drawer. */
  function renderCart(cart, quiet, generation) {
    var ids = gatherGroupedSectionIds();
    return fetchSectionsHtml(ids).then(function (sections) {
      if (generation !== refreshGeneration) return;
      var drawers = quiet ? document.querySelectorAll('cart-drawer-component[auto-open]') : [];
      drawers.forEach(function (d) { d.removeAttribute('auto-open'); });
      try { document.dispatchEvent(cartUpdateEvent(cart, sections)); } catch (e) {}
      finally { drawers.forEach(function (d) { d.setAttribute('auto-open', ''); }); }
      /* The event's `sections` let the drawer morph its own content natively;
         we only ensure the drawer is actually open. */
      if (!quiet) forceOpenDrawer();
    });
  }

  function refreshAndOpen(cart) {
    return renderCart(cart, false, ++refreshGeneration);
  }

  /* Same refresh, but the drawer stays shut - for surfaces that should add to
     the cart and leave the customer exactly where they are (the PDP sticky bar).
     The count bubble and the drawer both read `itemCount` off this event, so the
     payload cannot simply omit it; instead the drawer's own `auto-open`
     attribute (its documented opt-out) is lifted for the dispatch. The dispatch
     is synchronous, so there is no window in which a genuine add could be
     missed. Cart count, drawer contents, free-shipping goal and every other
     cart:update listener still update normally. */
  function refreshQuiet(cart) {
    return renderCart(cart, true, ++refreshGeneration);
  }

  /* Refresh the cart surfaces without opening the drawer. */
  function refresh() {
    var generation = ++refreshGeneration;
    return origFetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('Cart refresh failed'); return r.json(); })
      .then(function (cart) { if (generation === refreshGeneration) return renderCart(cart, true, generation); })
      .catch(function () {});
  }

  /* Open the drawer immediately for snappy UX, then refresh its contents in the
     background. */
  function open() {
    var generation = ++refreshGeneration;
    forceOpenDrawer();
    return origFetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('Cart refresh failed'); return r.json(); })
      .then(function (cart) { if (generation === refreshGeneration) return renderCart(cart, false, generation); })
      .catch(function () {});
  }

  /* Add items to the cart, then refresh + open the drawer.
     Returns a promise resolving to the /cart/add.js response, or rejecting
     with the error payload so callers can show their own error message. */
  function addAndOpen(items) {
    return origFetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) return Promise.reject(res.data);
        // Reconcile the free gift BEFORE the drawer renders so it opens straight
        // to the final state (correct gift + tier), never a mid-swap flash. This
        // add used the original fetch, so the engine's interceptor didn't run.
        var reconciled = (window.AquonBundleEngine && window.AquonBundleEngine.reconcile)
          ? window.AquonBundleEngine.reconcile().catch(function () {})
          : Promise.resolve();
        return reconciled.then(open).then(function () { return res.data; });
      });
  }

  window.AquonCart = {
    refreshAndOpen: refreshAndOpen,
    open: open,
    addAndOpen: addAndOpen,
    refresh: refresh,
    refreshQuiet: refreshQuiet
  };
})();
